import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { onpRequest } from '../../util';
import { translate } from '../../i18n/customI18nProvider';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxWidth: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 2
};

const OrderDetailsModal = ({ orderId, userId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) return;

    setLoading(true);
    loadOrderDetails();

  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
        const response = await onpRequest(userId, '/order/' + orderId, 'GET');
        if(response.client) {
            let transaction = null;
            let creditTransaction = null;
            for(const t of response.client.transactions) {
                if(t.type == 'debit') {
                    transaction = t;
                } else if(t.type == 'credit') {
                    creditTransaction = t;
                }
            }

            const orderData = {
                eventName: response.event_name,
                barName: response.bar_name,
                number: transaction?.order_number ?? "-",
                createdAt: transaction?.date ?? "-",
                total: transaction?.amount ?? 0,
                items: transaction?.order ?? [],
                tableNumber: transaction?.table_number ?? null,
                clientName: response.client?.name ?? null,
                paymentType: creditTransaction?.payment_type ? translate('custom.payment_types_description.' + creditTransaction.payment_type.toUpperCase()) : null,
                referenceNumber: creditTransaction?.reference_number ?? creditTransaction?.referenceNumber ?? creditTransaction?.twint_direct_merchant_tran_id ?? null,
            }
            setOrder(orderData);
        } else {
            console.error('Unexpected response format:', response);
            setError(response);
        }
    } catch (error) {
        setError(error);
        console.error('Error fetching order details:', error);
    } finally {
        setLoading(false);
    }
  };

  return (
    <Modal open={!!orderId} onClose={onClose} sx={{ zIndex: 1600 }}>
      <Box sx={style}>
        {loading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{translate("custom.error_loading_order_details")}</Typography>
        ) : order ? (
          <>
            <Typography variant="h6" align="center">
              {order.barName ?? '-'}
            </Typography>
            <Typography variant="subtitle1" align="center">
              {order.eventName ?? '-'}
            </Typography>
            <Typography variant="h4" align="center">
              {order.number ?? order.uid?.slice(0, 8) ?? '-'}
            </Typography>
            <List sx={{ maxHeight: 'calc(80vh - 300px)', overflowY: 'auto'}}>
              {order.items.map((item, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                        <Typography variant="subtitle1">{`${item.quantity} x ${item.name}`}</Typography>
                        <Typography sx={{flex: '0 0 auto'}} variant="subtitle1">{`CHF ${item.total.toFixed(2)}`}</Typography>
                    </div>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 1 }} />

            <Typography variant="h6" align="right">
              {translate("custom.total")} : CHF {order.total.toFixed(2)}
            </Typography>

            <Typography variant="body2" align="right" color="text.secondary">
              {order.createdAt}
            </Typography>

            {order.tableNumber ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                    <Typography variant="h6">{translate("custom.table_number_ticket")}</Typography>
                    <Typography sx={{flex: '0 0 auto'}} variant="h6">{order.tableNumber}</Typography>
                </div>
            ) : null}

            {order.clientName ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                    <Typography variant="subtitle1">{translate("custom.last_name")}</Typography>
                    <Typography sx={{flex: '0 0 auto'}} variant="subtitle1">{order.clientName}</Typography>
                </div>
            ) : null}

            {order.paymentType ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                    <Typography variant="subtitle1">{translate("custom.payment_type")}</Typography>
                    <Typography sx={{flex: '0 0 auto'}} variant="subtitle1">{order.paymentType}</Typography>
                </div>
            ) : null}

            {order.referenceNumber ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                    <Typography variant="subtitle1">{translate("custom.reference_number")}</Typography>
                    <Typography sx={{flex: '0 0 auto'}} variant="subtitle1">{order.referenceNumber}</Typography>
                </div>
            ) : null}

          </>
        ) : null}
      </Box>
    </Modal>
  );
};

export default OrderDetailsModal;